import { bip39Word } from "../bip39";
import { asBip39WordIndices, BLEND_SEED_LENGTH } from "../scoring";
import type { Blend, Puzzle } from "./types";

const INDICES_DOMAIN = "cogquest/v1/indices:";
const SEED_DOMAIN = "cogquest/v1/seed:";

/**
 * Derives a {@link Puzzle} deterministically from an opaque id string.
 *
 * Two independent SHA-256 hashes keyed off the id produce (a) the five
 * BIP-39 indices the player sees as the blend, and (b) the 32-byte blend
 * seed fed to the scorer. The two domain-separation prefixes keep them
 * uncorrelated, so a future change to either derivation can happen
 * without disturbing the other.
 *
 * The id is also the {@link Puzzle.id} — its storage key. Callers pick
 * the id namespace: the daily source uses `YYYY-MM-DD` (UTC); the random
 * source uses a `random-<seed>` token. Both stay opaque to storage.
 */
export async function derivePuzzleFromId(id: string): Promise<Puzzle> {
  const [indicesHash, blendSeed] = await Promise.all([
    sha256(utf8(INDICES_DOMAIN + id)),
    sha256(utf8(SEED_DOMAIN + id)),
  ]);
  const blendIndices = readFiveU16Mod2048(indicesHash);
  const blend = blendIndices.map(bip39Word) as unknown as Blend;
  return { id, blend, blendIndices, blendSeed };
}

async function sha256(data: Uint8Array<ArrayBuffer>): Promise<Uint8Array> {
  const buf = await crypto.subtle.digest("SHA-256", data);
  const out = new Uint8Array(BLEND_SEED_LENGTH);
  out.set(new Uint8Array(buf));
  return out;
}

function utf8(s: string): Uint8Array<ArrayBuffer> {
  return new TextEncoder().encode(s);
}

function readFiveU16Mod2048(seed: Uint8Array) {
  // Five u16s (10 bytes) read little-endian out of the seed. u16 % 2048
  // is uniform because 65536 is an exact multiple of 2048 — no modulo
  // bias.
  const view = new DataView(seed.buffer, seed.byteOffset, seed.byteLength);
  return asBip39WordIndices([
    view.getUint16(0, true) % 2048,
    view.getUint16(2, true) % 2048,
    view.getUint16(4, true) % 2048,
    view.getUint16(6, true) % 2048,
    view.getUint16(8, true) % 2048,
  ]);
}
